/**
 * CoherenceValidationSection
 *
 * Displays category coherence validation results checking that
 * product alternatives are from compatible categories.
 *
 * Part of Story 52: Category Coherence Validation
 *
 * Date: December 23, 2025
 */

import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import {
  AlertCircle,
  CheckCircle,
  AlertTriangle,
  Loader2,
  RefreshCw,
  Layers,
  ArrowRightLeft,
  XCircle,
} from 'lucide-react';
import {
  CoherenceValidationReport,
  validateCoherence,
  getCoherenceReport,
  getCoherenceScoreColor,
  getCoherenceSeverityColor,
} from '@/services/contentReviewService';

interface CoherenceValidationSectionProps {
  generationId: string;
  autoRun?: boolean;
}

export function CoherenceValidationSection({
  generationId,
  autoRun = false,
}: CoherenceValidationSectionProps) {
  const [report, setReport] = useState<CoherenceValidationReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasChecked, setHasChecked] = useState(false);
  const [skipped, setSkipped] = useState(false);
  const [skipReason, setSkipReason] = useState<string | null>(null);

  // Load existing report on mount
  useEffect(() => {
    async function loadExistingReport() {
      try {
        const response = await getCoherenceReport(generationId);
        if (response.data) {
          setReport(response.data);
          setHasChecked(true);
        } else if (autoRun) {
          handleRunValidation();
        }
      } catch (err) {
        console.error('Error loading coherence report:', err);
        if (autoRun) {
          handleRunValidation();
        }
      }
    }

    loadExistingReport();
  }, [generationId, autoRun]);

  async function handleRunValidation() {
    setLoading(true);
    setError(null);
    setSkipped(false);

    try {
      const response = await validateCoherence(generationId);
      if (response.skipped) {
        setSkipped(true);
        setSkipReason(response.reason || 'Validation skipped');
        setHasChecked(true);
      } else if (response.data) {
        setReport(response.data);
        setHasChecked(true);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to run validation');
    } finally {
      setLoading(false);
    }
  }

  // Render coherence icon
  const CoherenceIcon = () => {
    if (!report) return null;

    if (report.coherenceScore === null) {
      return <AlertCircle className="h-5 w-5 text-gray-500" />;
    }
    if (report.coherenceScore === 100) {
      return <CheckCircle className="h-5 w-5 text-green-500" />;
    }
    if (report.coherenceScore >= 70) {
      return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
    }
    return <XCircle className="h-5 w-5 text-red-500" />;
  };

  // If no check has been run yet
  if (!hasChecked && !loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Layers className="h-5 w-5" />
            Category Coherence
          </CardTitle>
          <CardDescription>
            Validate alternatives are from compatible product categories
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={handleRunValidation} disabled={loading}>
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Validating...
              </>
            ) : (
              'Check Category Coherence'
            )}
          </Button>
          {error && <p className="mt-2 text-sm text-red-500">{error}</p>}
        </CardContent>
      </Card>
    );
  }

  // Loading state
  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Loader2 className="h-5 w-5 animate-spin" />
            Checking Category Coherence...
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Extracting alternatives and validating category compatibility...
          </p>
        </CardContent>
      </Card>
    );
  }

  // Skipped state
  if (skipped) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-muted-foreground">
            <Layers className="h-5 w-5" />
            Category Coherence Skipped
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">{skipReason}</p>
          <Button variant="outline" onClick={handleRunValidation}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Error state
  if (error && !report) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-red-500">
            <AlertCircle className="h-5 w-5" />
            Coherence Check Error
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-red-500 mb-4">{error}</p>
          <Button variant="outline" onClick={handleRunValidation}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Report display
  if (!report) return null;

  const hasAlternatives = report.extractedAlternatives.length > 0;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <CoherenceIcon />
            Category Coherence
          </CardTitle>
          <div className="flex items-center gap-2">
            {report.severity && (
              <Badge variant={getCoherenceSeverityColor(report.severity)}>
                {report.severity}
              </Badge>
            )}
            {report.coherenceScore !== null && (
              <Badge variant={report.coherenceScore === 100 ? 'default' : 'secondary'}>
                {report.coherenceScore}% Coherent
              </Badge>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRunValidation}
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
        <CardDescription>{report.message}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Coherence Score */}
        {report.coherenceScore !== null && hasAlternatives && (
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className={getCoherenceScoreColor(report.coherenceScore)}>
                {report.compatibleAlternatives.length} of {report.extractedAlternatives.length} alternatives compatible
              </span>
            </div>
            <Progress value={report.coherenceScore} className="h-3" />
          </div>
        )}

        {/* Product Category Info */}
        <div className="p-3 bg-muted rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Layers className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Product Category</span>
            </div>
            <Badge variant="outline">{report.productCategoryDisplay}</Badge>
          </div>
          {report.discipline && (
            <p className="mt-1 text-xs text-muted-foreground">
              Discipline: {report.discipline}
            </p>
          )}
        </div>

        {/* No Alternatives */}
        {!hasAlternatives && (
          <div className="p-3 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-200 dark:border-green-800">
            <p className="text-sm text-green-700 dark:text-green-300">
              No alternative products mentioned in content - nothing to validate
            </p>
          </div>
        )}

        {/* Compatible Alternatives */}
        {report.compatibleAlternatives.length > 0 && (
          <div className="space-y-2">
            <h4 className="font-medium text-sm flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-500" />
              Compatible Alternatives ({report.compatibleAlternatives.length})
            </h4>
            <div className="space-y-1">
              {report.compatibleAlternatives.map((alt, index) => (
                <div
                  key={index}
                  className="p-2 rounded-lg border bg-green-50 border-green-200 dark:bg-green-950/20 dark:border-green-800"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">
                      {alt.brand ? `${alt.brand} ` : ''}{alt.name}
                    </span>
                    <Badge variant="outline" className="text-xs text-green-700">
                      {alt.categoryDisplay || alt.category}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Incompatible Alternatives (Issues) */}
        {report.issues.length > 0 && (
          <div className="space-y-2">
            <h4 className="font-medium text-sm flex items-center gap-2">
              <XCircle className="h-4 w-4 text-red-500" />
              Incompatible Alternatives ({report.issues.length})
            </h4>
            <div className="space-y-2">
              {report.issues.map((issue, index) => (
                <div
                  key={index}
                  className="p-3 rounded-lg border bg-red-50 border-red-200 dark:bg-red-950/20 dark:border-red-800"
                >
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">
                        {issue.brand ? `${issue.brand} ` : ''}{issue.alternative}
                      </span>
                      <Badge variant={getCoherenceSeverityColor(issue.severity)} className="text-xs">
                        {issue.severity}
                      </Badge>
                    </div>
                    <Badge variant="destructive" className="text-xs">
                      {issue.category}
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">{issue.reason}</p>
                  {issue.excerpt && (
                    <p className="text-xs text-muted-foreground mt-1 italic">
                      "{issue.excerpt}"
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Compatible Categories Reference - only show if backend returns this field */}
        {report.compatibleCategories?.length > 0 && report.compatibleCategories[0] !== '*' && (
          <details className="space-y-2">
            <summary className="cursor-pointer font-medium text-sm flex items-center gap-2">
              <ArrowRightLeft className="h-4 w-4 text-blue-500" />
              Compatible Categories for {report.productCategoryDisplay}
            </summary>
            <div className="mt-2 flex flex-wrap gap-2 pl-6">
              {report.compatibleCategories.map((cat, index) => (
                <Badge key={index} variant="outline" className="text-xs">
                  {cat}
                </Badge>
              ))}
            </div>
          </details>
        )}

        {/* Timestamp */}
        <p className="text-xs text-muted-foreground text-right">
          Validated: {new Date(report.validatedAt).toLocaleString()}
        </p>
      </CardContent>
    </Card>
  );
}
